// ─── CHARACTER FACTS LEDGER ──────────────────────────────────────────────────
//
// A small structured store of "things this character has committed to in
// conversation with this user" — wedding dates, child names, established
// promises, scars, possessions. Loaded into the system prompt as a LOCKED
// FACTS section so the model can't paraphrase or invent around them.
//
// Extraction runs after each AI response: a small Claude pass reads the
// recent turn pair and proposes new facts (or confirms existing ones). The
// extraction is fire-and-forget and uses the same personal_api_key that
// served the main response — costs are tiny (a few hundred tokens).
//
// The admin viewer (in AdminPanel) lets you list/edit/lock/delete facts.

import { supabase, safeGetSession } from './supabaseClient.js';
import { fetchPaged } from './pagedQuery.js';
import { resolveSubject } from './storyMemory.js';
import { AI_ABORT_MS } from './aiTimeouts.js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;

// ─── READ ─────────────────────────────────────────────────────────────────────

/**
 * Get all facts the AI has committed to for this character + user pair.
 * Returns recency-sorted, locked-first.
 */
export async function getCharacterFacts(characterId, userId, limit = 40) {
  if (!characterId || !userId) return [];
  try {
    const { data } = await supabase
      .from('character_facts')
      .select('id, fact_key, fact_value, fact_category, confidence, locked, last_confirmed, last_updated, notes')
      .eq('character_id', characterId)
      .eq('user_id', userId)
      .order('locked', { ascending: false })
      .order('last_confirmed', { ascending: false })
      .limit(limit);
    return data || [];
  } catch (err) {
    console.error('[characterFacts] getCharacterFacts error:', err);
    return [];
  }
}

/**
 * Format facts into a system-prompt section. Empty string when none.
 */
export function formatFactsForPrompt(facts, characterName) {
  if (!facts || facts.length === 0) return '';
  // Group by category for legibility
  const byCat = {};
  for (const f of facts) {
    const cat = f.fact_category || 'other';
    if (!byCat[cat]) byCat[cat] = [];
    byCat[cat].push(f);
  }
  const order = ['identity', 'relationship', 'event', 'physical', 'possession', 'location', 'preference', 'other'];
  const parts = [];
  for (const cat of order) {
    if (!byCat[cat]) continue;
    const lines = byCat[cat].map(f => {
      const lockedMark = f.locked ? ' 🔒' : '';
      const asOf = String(f.last_confirmed || f.last_updated || '').slice(0, 10);
      return `  • ${f.fact_key}: ${f.fact_value}${asOf ? ` (as of ${asOf})` : ''}${lockedMark}`;
    });
    parts.push(`${cat.toUpperCase()}:\n${lines.join('\n')}`);
  }
  return `\n═══ LOCKED FACTS — exact details ${characterName} has established with this person (treat as ground truth) ═══\nThese facts are CANON for this player. Use them verbatim. NEVER paraphrase, NEVER round, NEVER invent alternatives. If a fact conflicts with what the played RP history in this prompt shows, the played history wins — these are extracted notes, not the story itself. If something below is wrong, the user will edit it — do not silently overwrite. Locked entries (🔒) are admin-confirmed and especially sticky. Each fact carries the date it was last confirmed: a fact is a thing you KNOW, not a scene that is happening now.\n\n${parts.join('\n\n')}\n`;
}

// ─── EXTRACTION ──────────────────────────────────────────────────────────────

const EXTRACTION_PROMPT = `You are a fact-extraction assistant for an adult fiction RP platform. Read the latest exchange between a player and a character and identify ANY new concrete facts the AI character has just committed to that should be locked in.

A fact is concrete + specific + checkable. Examples:
- "wedding_date: October 14"
- "daughter_name: Emma"
- "scar: jagged scar across left palm from bonding ritual"
- "promised: never to lie about Moros again"
- "lives_in: underground vampire kingdom beneath the Carpathians"
- "hates: cold weather, especially mornings"

NOT facts: tone observations, emotional weather, vague impressions, restatements of the player's words, generic personality.

Return STRICT JSON: an array of {key, value, category} objects. category is one of: identity, relationship, event, preference, physical, possession, location, other.

Return an empty array [] if there are no new concrete facts.

Examples of good output:
[{"key":"daughter_name","value":"Emma","category":"relationship"},{"key":"first_met_year","value":"1847","category":"event"}]
[]

Output ONLY the JSON. No prose. No markdown fences.`;

/**
 * Extract facts from a recent turn pair and upsert them into the ledger.
 * Fire-and-forget — caller doesn't await.
 *
 * @param {object} params
 * @param {string} params.characterId
 * @param {string} params.userId
 * @param {string} params.characterName  - for prompt context
 * @param {string} params.userMessage    - what the player just said
 * @param {string} params.aiResponse     - what the character just said
 * @param {string} params.sourceTable    - 'dm_messages' | 'story_messages' | 'channel_messages'
 * @param {string} params.sourceMessageId - optional message id this came from
 */
export async function extractAndStoreFacts(params) {
  const { characterId, userId, characterName, userMessage, aiResponse, sourceTable, sourceMessageId, cast = [] } = params;
  // WHOSE LEDGER. Same fix extractStoryEvents got in v261, and the reason this
  // stayed gated to single-character scenes in v268: every row was written with
  // the one characterId passed in, so in an ensemble a promise Trix made would
  // have been recorded as Cruz's — worse than recording nothing.
  //
  // getCharacterFacts reads this table per character, and v257 made ensemble
  // prompts load supporting characters' ledgers. Those ledgers could not exist
  // until this wrote them.
  const roster = Array.isArray(cast) ? cast.filter(c => c?.id && c?.name) : [];
  const multiCast = roster.length > 1;
  if (!characterId || !userId || !aiResponse) return;
  try {
    const { data: { session } = { session: null } } = await safeGetSession().catch(() => ({ data: { session: null } }));
    if (!session?.access_token) return; // can't auth → bail silently

    const turn = `PLAYER: ${String(userMessage || '').slice(0, 1500)}\n\n${characterName.toUpperCase()}: ${String(aiResponse).slice(0, 3000)}`;

    const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-proxy`, {
      method: 'POST',
      signal: AbortSignal.timeout(AI_ABORT_MS), // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js)
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        // Kept on Claude (with nsfw:true) because the turn pair may contain NSFW
        // content; Gemini's HARASSMENT/DANGEROUS_CONTENT filters can mangle
        // extraction on darker scenes (noncon, violence) even though sexually
        // explicit content itself is BLOCK_NONE on Gemini. Cost trade-off: ~$10/mo
        // for guaranteed extraction reliability across all content tones.
        provider: 'claude-haiku',
        nsfw: true,
        systemPrompt: multiCast
          ? `${EXTRACTION_PROMPT}\n\nWHOSE FACT IS IT — REQUIRED. This scene voices several characters: ${roster.map(c => c.name).join(', ')}. Add a "subject" to every object naming the ONE whose fact it is — who owns the possession, made the promise, holds the relationship, has the scar. Use the name exactly as written above. A fact about the player belongs to the character who now knows it, not to the player.`
          : EXTRACTION_PROMPT,
        messages: [{ role: 'user', content: turn }],
        // Headroom, not permission (the v161 lesson, repeated live): a player
        // on a BYOK key routes even background features to THEIR model, and a
        // reasoning model spends hidden tokens before emitting any text. At
        // 400 the budget was exhausted first, so extraction returned
        // finish_reason=length with no output and the facts were silently
        // never learned. The prompt still bounds the real answer.
        maxTokens: 1024,
        feature: 'fact_extraction',
        characterId,
        characterName,
      }),
    });
    if (!res.ok) return; // silent fail — facts are optional
    const data = await res.json();
    const text = (data.text || '').trim();
    if (!text) return;
    let parsed;
    try { parsed = JSON.parse(text); } catch { return; }
    if (!Array.isArray(parsed) || parsed.length === 0) return;

    // Upsert each — touches last_confirmed even when nothing changes.
    const now = new Date().toISOString();
    for (const f of parsed) {
      if (!f?.key || !f?.value) continue;
      const key = String(f.key).slice(0, 80).toLowerCase().replace(/\s+/g, '_');
      const value = String(f.value).slice(0, 500).trim();
      const category = ['identity','relationship','event','preference','physical','possession','location','other']
        .includes(f.category) ? f.category : 'other';
      try {
        // Try update first (preserves locked + notes); insert if new.
        const owner = resolveSubject(f.subject, roster, characterId);
        const { data: existing } = await supabase
          .from('character_facts')
          .select('id, locked, fact_value')
          .eq('character_id', owner)
          .eq('user_id', userId)
          .eq('fact_key', key)
          .maybeSingle();
        if (existing) {
          // Don't overwrite locked facts. Just bump confirmation.
          if (existing.locked) {
            await supabase
              .from('character_facts')
              .update({ last_confirmed: now })
              .eq('id', existing.id);
          } else {
            await supabase
              .from('character_facts')
              .update({ fact_value: value, fact_category: category, last_confirmed: now, last_updated: now,
                        source_message_id: sourceMessageId || null, source_table: sourceTable || null })
              .eq('id', existing.id);
          }
        } else {
          await supabase.from('character_facts').insert({
            character_id: owner, user_id: userId,
            fact_key: key, fact_value: value, fact_category: category,
            source_message_id: sourceMessageId || null, source_table: sourceTable || null,
            first_seen: now, last_confirmed: now, last_updated: now,
          });
        }
      } catch (upsertErr) {
        // Silent — extraction is best-effort
      }
    }
  } catch (err) {
    console.warn('[characterFacts] extraction error (non-fatal):', err);
  }
}

// ─── ADMIN HELPERS ───────────────────────────────────────────────────────────

export async function listAllFactsForAdmin() {
  try {
    // Paged: the old 2,000-row limit silently returned 1,000 of 5,533 (2026-10-19 audit).
    const { rows, error } = await fetchPaged((from, to) => supabase
      .from('character_facts')
      .select('id, character_id, user_id, fact_key, fact_value, fact_category, confidence, locked, first_seen, last_confirmed, last_updated, notes')
      .order('character_id', { ascending: true })
      .order('user_id', { ascending: true })
      .order('locked', { ascending: false })
      .order('last_confirmed', { ascending: false })
      .order('id', { ascending: true })
      .range(from, to), { max: 20000 });
    if (error) console.error('[characterFacts] listAllFactsForAdmin page error:', error.message);
    return rows;
  } catch (err) {
    console.error('[characterFacts] listAllFactsForAdmin error:', err);
    return [];
  }
}

export async function setFactLocked(id, locked) {
  return supabase.from('character_facts').update({ locked, last_updated: new Date().toISOString() }).eq('id', id);
}

export async function deleteFact(id) {
  return supabase.from('character_facts').delete().eq('id', id);
}

export async function updateFactValue(id, value) {
  return supabase.from('character_facts').update({ fact_value: value, last_updated: new Date().toISOString() }).eq('id', id);
}
